import tokens from '@kluein/design-tokens';
import { Box, Button, List, Tip } from '@kluein/klue-ui';
import { CaretDown, Eye } from '@phosphor-icons/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import store from 'store';
import TRACKING_IDS from 'tracking-ids';

import type { Dispatch, RootState } from 'store/store.types';

type ButtonProps = {
  iconOnly?: boolean;
  dataTrackingId?: string;
  disabled?: boolean;
  title?: string;
};

export default function ViewAsDropdown({
  iconOnly,
  dataTrackingId = TRACKING_IDS.dashboard.curatorControls.viewAs,
  disabled = false,
  title,
}: ButtonProps) {
  const { t } = useTranslation();
  const {
    visibilityGroups: { fetchVisibilityGroupsFromApi },
  } = useDispatch<Dispatch>();

  useEffect(() => {
    fetchVisibilityGroupsFromApi();
  }, [fetchVisibilityGroupsFromApi]);

  const viewAsOptions = useSelector((state: RootState) =>
    store.select.visibilityGroups.viewAs(state).map((group) => {
      const url = new URL(window.location.href);
      url.searchParams.set('previewing', group.id.toString());
      const href = url.href;
      return {
        key: group.id.toString(),
        label:
          'name' in group
            ? group.name
            : t('Common:visibilityTypeTitles.fullAccessUsers'),
        href,
      };
    }),
  );

  // eslint-disable-next-line no-nested-ternary
  const tip = title
    ? { content: title }
    : iconOnly
    ? { content: t('Dashboard:curatorControls.viewAs') }
    : undefined;

  const button = (
    <Box flex={false}>
      <Button
        variant="flat"
        a11yTitle={title}
        label={iconOnly ? undefined : t('Dashboard:curatorControls.viewAs')}
        leftSlot={<Eye />}
        rightSlot={<CaretDown size={tokens.iconSize.small} />}
        dropContent={
          <Box pad="small">
            <List
              pad="xsmall"
              a11yTitle="View As Options"
              data={viewAsOptions}
              itemProps={{ target: '_blank' }}
            />
          </Box>
        }
        disabled={disabled}
        data-tracking-id={dataTrackingId}
      />
    </Box>
  );

  return tip ? <Tip content={tip?.content}>{button}</Tip> : button;
}
